import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  listGrievances,
  type FilterStatus,
  type GrievanceListItem,
  type SortDir,
} from "@/api/admin";
import { EmptyState } from "@/components/EmptyState";
import { Pagination } from "@/components/Pagination";
import { Spinner } from "@/components/Spinner";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateOnly } from "@/lib/formatDate";
import { AdminHeader } from "@/pages/admin/components/AdminHeader";
import { FilterBar } from "@/pages/admin/components/FilterBar";
import { useCurrentTabId } from "@/pages/admin/TabsContext";

const PAGE_SIZE = 50;
const VALID_FILTERS: FilterStatus[] = ["Pending", "Acknowledged", "Attachments"];

function parseStatus(sp: URLSearchParams): FilterStatus[] {
  return sp
    .getAll("status")
    .filter((s): s is FilterStatus => (VALID_FILTERS as string[]).includes(s));
}

function parsePage(sp: URLSearchParams): number {
  const n = parseInt(sp.get("page") ?? "1", 10);
  return Number.isFinite(n) && n >= 1 ? n : 1;
}

function parseSort(sp: URLSearchParams): SortDir {
  return sp.get("sort") === "asc" ? "asc" : "desc";
}

export function TicketsListPage() {
  const [sp, setSp] = useSearchParams();
  const status = parseStatus(sp);
  const q = sp.get("q") ?? "";
  const page = parsePage(sp);
  const sort = parseSort(sp);
  const offset = (page - 1) * PAGE_SIZE;

  const nav = useNavigate();
  const qc = useQueryClient();
  const tabId = useCurrentTabId();

  const query = useQuery({
    queryKey: ["grievances", tabId, { status, q, page, sort }],
    queryFn: () =>
      listGrievances(tabId, { status, q, limit: PAGE_SIZE, offset, sort }),
    placeholderData: keepPreviousData,
  });

  const items: GrievanceListItem[] = useMemo(() => query.data?.items ?? [], [query.data]);
  // Forward status + q into the detail URL so Prev/Next and post-Send navigation
  // stay inside the current filtered list. `page` is intentionally excluded –
  // the detail page walks the whole filtered result set, not one page of it.
  const detailSuffix = useMemo(() => {
    const u = new URLSearchParams();
    status.forEach((s) => u.append("status", s));
    if (q) u.set("q", q);
    if (sort === "asc") u.set("sort", "asc");
    const s = u.toString();
    return s ? `?${s}` : "";
  }, [status, q, sort]);
  const total = query.data?.total_estimate ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = offset + items.length;

  function applyFilters(next: { status: FilterStatus[]; q: string }) {
    const u = new URLSearchParams();
    next.status.forEach((s) => u.append("status", s));
    if (next.q) u.set("q", next.q);
    // Sort choice must survive a filter change – the user explicitly picked
    // an order and expects it to stay put.
    if (sort === "asc") u.set("sort", "asc");
    // Reset to page 1 whenever the filter set changes.
    setSp(u, { replace: true });
  }

  function toggleSort() {
    const u = new URLSearchParams(sp);
    if (sort === "asc") {
      u.delete("sort"); // desc is the default; keep the URL clean
    } else {
      u.set("sort", "asc");
    }
    // Page numbers only make sense within a specific order; reset to 1.
    u.delete("page");
    setSp(u, { replace: true });
  }

  function goToPage(next: number) {
    const u = new URLSearchParams(sp);
    if (next <= 1) u.delete("page");
    else u.set("page", String(next));
    setSp(u, { replace: false });
    // Scroll to top of list for predictability.
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function onRefresh() {
    void qc.invalidateQueries({ queryKey: ["grievances"] });
  }

  // If the URL points to a page beyond the current result set, snap back to 1.
  if (page > 1 && !query.isLoading && total > 0 && offset >= total) {
    const u = new URLSearchParams(sp);
    u.delete("page");
    setSp(u, { replace: true });
  }

  return (
    <div className="min-h-screen flex flex-col">
      <AdminHeader title="Admin Ticket Management Dashboard" />
      <main className="w-full px-4 sm:px-6 lg:px-10 xl:px-14 py-6 flex-1 flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-xl font-semibold">Tickets Queue</h2>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {total === 0 ? (
              "No tickets."
            ) : (
              <>
                Showing{" "}
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {total.toLocaleString()}
                </span>{" "}
                tickets · page{" "}
                <span className="font-semibold text-slate-700 dark:text-slate-200">
                  {Math.min(page, totalPages)}
                </span>{" "}
                of {totalPages.toLocaleString()}
              </>
            )}
          </div>
        </div>

        <FilterBar
          status={status}
          q={q}
          onChange={applyFilters}
          onRefresh={onRefresh}
          isRefetching={query.isFetching}
        />

        {query.isLoading ? (
          <div className="card p-12 flex justify-center">
            <Spinner label="Loading tickets…" />
          </div>
        ) : query.isError ? (
          <div className="alert-danger">
            {(query.error as Error)?.message ?? "Failed to load tickets."}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title="No tickets match the current filter."
            description="Try clearing the search or status filter."
          />
        ) : (
          <div className="card flex flex-col">
            {/* Table header – generous spacing, left-aligned. The same grid
                is reused per row so columns line up exactly. */}
            <div
              className="
                grid grid-cols-[10rem_minmax(0,1fr)_8rem_9rem_10rem_6rem]
                lg:grid-cols-[12rem_minmax(0,1fr)_9rem_11rem_12rem_7rem]
                gap-x-8 lg:gap-x-12 px-6 lg:px-8 py-3
                text-[11px] font-semibold uppercase tracking-wider
                text-slate-500 dark:text-slate-400
                border-b border-canvas-border dark:border-surface-border
              "
            >
              <span className="text-left">Application ID</span>
              <span className="text-left">Ticket</span>
              <span className="text-left">Status</span>
              <span className="text-left">
                <button
                  type="button"
                  onClick={toggleSort}
                  className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-700 dark:hover:text-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2 focus:ring-offset-canvas dark:focus:ring-offset-surface rounded"
                  title={sort === "asc" ? "Currently oldest first. Click to switch to newest first." : "Currently newest first. Click to switch to oldest first."}
                  aria-label={sort === "asc" ? "Sort: oldest first (click to reverse)" : "Sort: newest first (click to reverse)"}
                >
                  Submitted
                  {sort === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
                </button>
              </span>
              <span className="text-left">Responded by</span>
              <span className="text-left">Action</span>
            </div>

            <ul role="list" className="divide-y divide-canvas-border dark:divide-surface-border/70">
              {items.map((item) => (
                <li
                  key={item.grievance_id}
                  className="
                    grid grid-cols-[10rem_minmax(0,1fr)_8rem_9rem_10rem_6rem]
                    lg:grid-cols-[12rem_minmax(0,1fr)_9rem_11rem_12rem_7rem]
                    gap-x-8 lg:gap-x-12 px-6 lg:px-8 py-4 items-center
                    hover:bg-brand-green/25 dark:hover:bg-brand-green/15
                    cursor-pointer transition-colors
                  "
                  role="button"
                  tabIndex={0}
                  onClick={() => nav(`/admin/tickets/${item.grievance_id}${detailSuffix}`)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      nav(`/admin/tickets/${item.grievance_id}${detailSuffix}`);
                    }
                  }}
                >
                  <span className="font-mono text-sm font-semibold text-brand-appId truncate text-left">
                    {item.application_id}
                  </span>
                  <span className="text-sm text-slate-700 dark:text-slate-300 truncate text-left">
                    {item.grievance_preview || <em className="text-slate-400">–</em>}
                  </span>
                  <span className="text-left">
                    <StatusBadge status={item.status} />
                  </span>
                  <span className="text-xs font-mono text-slate-500 dark:text-slate-400 text-left">
                    {formatDateOnly(item.sent_date_time)}
                  </span>
                  <span className="text-left text-xs text-slate-600 dark:text-slate-300 truncate" title={item.responded_by_full_name ?? item.responded_by_username ?? ""}>
                    {item.responded_by_username ? (
                      <>
                        <span className="font-medium">{item.responded_by_full_name || item.responded_by_username}</span>
                        {item.responded_action_type === "edited_submitted" && (
                          <span className="ml-1 text-[10px] text-amber-600">(edited)</span>
                        )}
                      </>
                    ) : (
                      <em className="text-slate-400">–</em>
                    )}
                  </span>
                  <span className="text-left">
                    <span className="btn-secondary text-xs px-2.5 py-1">
                      View <ChevronRight size={12} />
                    </span>
                  </span>
                </li>
              ))}
            </ul>

            <div className="sticky bottom-0 z-10 border-t border-canvas-border dark:border-surface-border bg-canvas-raised dark:bg-surface-raised rounded-b-lg">
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={goToPage}
                disabled={query.isFetching}
              />
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
